import config from "@/constants/Config";
import orientationStateClient from "@apis/orientationState/orientationState";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { MappedStateDto } from "@apis/stateMappingClient/stateMappingClient.dto";
import { stateLabel } from "@/utils/stateLabelUtils";
import { stateColorFill } from "@/utils/stateColorUtils";

export interface OrientationCustomState {
  key: string;
  name?: string;
  image?: string;
}

export interface OrientationStateConfiguration {
  allowCustomMapping?: boolean;
  customStates?: OrientationCustomState[];
}

export interface OrientationStateInfo {
  key: string;
  name: string;
  image: string;
  /** the color charts draw the state in: its palette color's fill (R5), else a built-in side's */
  color?: string;
  /** STIC-16316 R5: the key of the state's color of the palette; its icon is drawn on a tile of it */
  stateColor?: string | null;
}

const SYSTEM_IMAGE_PATH = "/rest/image_library/system/";

const BUILT_IN_LANGUAGE_KEYS = new Map<string, LanguageKey>([
  ["top_up", LanguageKey.state_top_up_key],
  ["bottom_up", LanguageKey.state_bottom_up_key],
  ["left_up", LanguageKey.state_left_up_key],
  ["right_up", LanguageKey.state_right_up_key],
  ["back_up", LanguageKey.state_back_up_key],
  ["front_up", LanguageKey.state_front_up_key],
  ["state1", LanguageKey.state1_key],
  ["state2", LanguageKey.state2_key],
  ["unknown", LanguageKey.unknown_key],
]);

// only these have a system image
const SYSTEM_STATE_COLORS = new Map<string, string>([
  ["top_up", "#FFCC66"],
  ["bottom_up", "#F57DFF"],
  ["left_up", "#212851"],
  ["right_up", "#88e0c2"],
  ["back_up", "#F68483"],
  ["front_up", "#950000"],
]);

// STIC-16316: the state's label and icon from the states that apply to the tracker (its own custom
// states, or the organization mapping) first, then the built-in translation, else the raw key
// (same rule as backend OrientationStateDisplay)
export const getOrientationStateInfo = (
  key: string,
  states?: MappedStateDto[] | null
): OrientationStateInfo => {
  const mapped = states?.find((state) => state.key === key);
  const languageKey = BUILT_IN_LANGUAGE_KEYS.get(key);
  const systemImage = SYSTEM_IMAGE_PATH + (SYSTEM_STATE_COLORS.has(key) ? key : "unknown");

  return {
    key,
    name: (mapped?.label && stateLabel(mapped)) || (languageKey ? translate(languageKey) : key),
    image: config.API_URL + (mapped?.image || systemImage),
    color: mapped ? stateColorFill(mapped.color) : SYSTEM_STATE_COLORS.get(key),
    stateColor: mapped?.color ?? null,
  };
};

/**
 * STIC-16316: the tracker's own orientation states, as MappedStateDto, when its
 * service configuration overrides the organization default; null on the default
 * (the organization mapping's states come from the device state-mapping endpoint)
 */
export const ownOrientationStates = (
  configuration?: { allowCustomMapping?: unknown; customStates?: unknown } | null
): MappedStateDto[] | null => {
  if (!configuration?.allowCustomMapping) return null;
  type OwnState = {
    key: string;
    name?: string;
    image?: string | null;
    translations?: Record<string, string> | null;
    color?: string | null;
  };
  const states = (configuration.customStates || []) as (Partial<OwnState> | null)[];
  return states
    .filter((state): state is OwnState => !!state && !!state.key)
    .map((state) => ({
      key: state.key,
      label: state.name || "",
      image: state.image ?? null,
      translations: state.translations ?? null,
      color: state.color ?? null,
    }));
};

export interface OrientationStateSelectOption {
  value: string;
  label: string;
}

const DEFAULT_STATE_KEYS = [
  "top_up",
  "bottom_up",
  "left_up",
  "right_up",
  "back_up",
  "front_up",
  "unknown",
  "state1",
  "state2",
];

export interface OrientationStateLabelOption {
  value: string;
  label: string;
  keys: string[];
  image?: string;
}

// what the state option endpoints return: a label, the keys behind it and the icon of the label
export interface StateOptionData {
  label: string;
  keys: string[];
  image?: string;
}

const fetchOrientationStateOptions = (includeDefault: boolean, orgId?: number | null) =>
  orientationStateClient
    .getOrientationStateOptions(includeDefault, orgId)
    .then(({ data }: { data: StateOptionData[] }) => data);

// options of different keys whose labels read the same (ignoring case) get the key appended, so they
// can be told apart - the same rule as the state filter options of the lists
const disambiguateStateLabels = (options: OrientationStateSelectOption[]): OrientationStateSelectOption[] => {
  const countByLabel = new Map<string, number>();
  options.forEach((option) => {
    const label = option.label.toLowerCase();
    countByLabel.set(label, (countByLabel.get(label) || 0) + 1);
  });
  return options.map((option) =>
    (countByLabel.get(option.label.toLowerCase()) || 0) > 1
      ? { ...option, label: option.label + " (" + option.value + ")" }
      : option
  );
};

// label options: selecting one stores every state key behind it. Labels backed by exactly the same
// keys become one option, so a key with two names shows one chip that can be removed on its own.
export const toStateLabelOptions = (data: StateOptionData[]): OrientationStateLabelOption[] => {
  const byKeys = new Map<string, OrientationStateLabelOption>();
  data.forEach((option) => {
    const keysId = [...option.keys].sort().join("\u0000");
    const existing = byKeys.get(keysId);
    if (existing) {
      existing.label = existing.label + " / " + option.label;
      existing.value = existing.label;
    } else {
      byKeys.set(keysId, { value: option.label, label: option.label, keys: option.keys, image: option.image });
    }
  });
  return [...byKeys.values()];
};

// one option per label. Custom mapped states of the org(s), plus the built-in states with includeDefault.
export const fetchOrientationStateLabelOptions = (
  includeDefault: boolean,
  orgId?: number | null
): Promise<OrientationStateLabelOption[]> =>
  fetchOrientationStateOptions(includeDefault, orgId).then(toStateLabelOptions);

// one option per state key (value = key), labelled with all names of that key (a name that differs only
// by case counts once)
export const toStateKeyOptions = (data: StateOptionData[]): OrientationStateSelectOption[] => {
  const labelsByKey = new Map<string, string[]>();
  data.forEach((option) =>
    option.keys.forEach((key) => {
      const labels = labelsByKey.get(key) || [];
      if (!labels.some((label) => label.toLowerCase() === option.label.toLowerCase())) {
        labelsByKey.set(key, [...labels, option.label]);
      }
    })
  );
  return disambiguateStateLabels(
    [...labelsByKey.entries()].map(([key, labels]) => ({ value: key, label: labels.join(" / ") }))
  ).sort((a, b) => a.label.localeCompare(b.label));
};

// one option per state key (value = key), labelled with all names of that key
export const fetchOrientationStateSelectOptions = (
  includeDefault: boolean,
  orgId?: number | null
): Promise<OrientationStateSelectOption[]> =>
  fetchOrientationStateOptions(includeDefault, orgId).then(toStateKeyOptions);

// stored keys -> label options fully covered by them, plus keys no option covers
export const toOrientationStateLabelSelection = (
  keys: string[],
  options: OrientationStateLabelOption[]
): OrientationStateLabelOption[] => {
  const selected = options.filter(
    (option) => option.keys.length > 0 && option.keys.every((key) => keys.includes(key))
  );
  const covered = new Set(selected.flatMap((option) => option.keys));
  const uncovered = keys
    .filter((key) => !covered.has(key))
    .map((key) => ({ value: key, label: getOrientationStateInfo(key).name, keys: [key] }));
  return [...selected, ...uncovered];
};

// selected label options -> the state keys to store (created options store their value)
export const toOrientationStateKeys = (
  selected: readonly { value: string; keys?: string[] }[]
): string[] => [...new Set(selected.flatMap((option) => option.keys || [option.value]))];

export const getDefaultOrientationStateSelectOptions = (): OrientationStateSelectOption[] =>
  DEFAULT_STATE_KEYS.map((key) => ({ value: key, label: getOrientationStateInfo(key).name }));

// stored keys -> their options; a key no option covers keeps fallbackLabel (by default the label of the
// orientation state)
export const toOrientationStateSelectedOptions = <T extends { value: string }>(
  keys: string[],
  options: T[],
  fallbackLabel: (key: string) => string = (key) => getOrientationStateInfo(key).name
): (T | OrientationStateSelectOption)[] =>
  keys.map((key) => options.find((option) => option.value === key) || { value: key, label: fallbackLabel(key) });
